import JSZip from "jszip";
import { saveAs } from "file-saver";
import { Photo } from "../types/api";

export interface DownloadProgress {
  current: number;
  total: number;
  percentage: number;
  currentFileName?: string;
}

export interface UsePhotoDownloadReturn {
  downloadSingle: (photo: Photo, filename?: string) => Promise<void>;
  downloadZip: (
    photos: Photo[],
    zipName: string,
    onProgress?: (progress: DownloadProgress) => void
  ) => Promise<{ successful: number; failed: number }>;
}

const DEFAULT_TIMEOUT = 30000; // 30 seconds per photo

/**
 * Get the best available photo URL with fallback logic
 * Prioritizes: finalUrl > previewUrl > originalUrl
 */
const getPhotoUrl = (photo: Photo): string => {
  return photo.finalUrl || photo.previewUrl || photo.originalUrl;
};

/**
 * Generate a filename from photo metadata or ID
 */
const generatePhotoFilename = (photo: Photo, index?: number): string => {
  if (photo.metadata?.originalFileName) {
    return photo.metadata.originalFileName;
  }

  // Fallback to photo_{index}.jpg
  const photoNumber = index !== undefined ? `_${String(index + 1).padStart(3, "0")}` : "";
  return `photo${photoNumber}.jpg`;
};

/**
 * Fetch a photo as a blob with timeout handling
 */
const fetchPhotoBlob = async (
  url: string,
  timeout: number = DEFAULT_TIMEOUT
): Promise<Blob> => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, {
      signal: controller.signal,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.blob();
  } finally {
    clearTimeout(timeoutId);
  }
};

/**
 * Custom hook for downloading photos individually or as a ZIP archive
 */
export const usePhotoDownload = (): UsePhotoDownloadReturn => {
  /**
   * Download a single photo
   */
  const downloadSingle = async (
    photo: Photo,
    filename?: string
  ): Promise<void> => {
    try {
      const photoUrl = getPhotoUrl(photo);

      if (!photoUrl) {
        throw new Error("No download URL available for this photo");
      }

      const blob = await fetchPhotoBlob(photoUrl);
      const finalFilename = filename || generatePhotoFilename(photo);

      saveAs(blob, finalFilename);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to download photo";
      console.error("Error downloading single photo:", message);
      throw new Error(`Failed to download photo: ${message}`);
    }
  };

  /**
   * Download multiple photos as a ZIP archive
   * Returns count of successful and failed downloads
   */
  const downloadZip = async (
    photos: Photo[],
    zipName: string,
    onProgress?: (progress: DownloadProgress) => void
  ): Promise<{ successful: number; failed: number }> => {
    const zip = new JSZip();
    let successful = 0;
    let failed = 0;

    try {
      // Download each photo
      for (let i = 0; i < photos.length; i++) {
        const photo = photos[i];

        try {
          // Report progress
          onProgress?.({
            current: i + 1,
            total: photos.length,
            percentage: Math.round(((i + 1) / photos.length) * 100),
            currentFileName: generatePhotoFilename(photo, i),
          });

          const photoUrl = getPhotoUrl(photo);

          if (!photoUrl) {
            console.warn(`No URL available for photo ${photo.id}`);
            failed++;
            continue;
          }

          const blob = await fetchPhotoBlob(photoUrl);
          const filename = generatePhotoFilename(photo, i);

          zip.file(filename, blob);
          successful++;
        } catch (error) {
          const errorMessage =
            error instanceof Error
              ? error.message
              : "Unknown error downloading photo";
          console.error(
            `Error downloading photo ${photo.id}:`,
            errorMessage
          );
          failed++;
          // Continue with next photo instead of throwing
        }
      }

      if (successful === 0) {
        throw new Error(
          "Failed to download any photos. Please try again later."
        );
      }

      // Generate ZIP file
      const zipBlob = await zip.generateAsync({ type: "blob" });
      saveAs(zipBlob, zipName);

      return { successful, failed };
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to create ZIP file";
      console.error("Error creating ZIP:", message);
      throw new Error(`Failed to download photos: ${message}`);
    }
  };

  return {
    downloadSingle,
    downloadZip,
  };
};
